import { ANNOTATION_CATEGORIES } from 'constants/annotation'

import {
  CommentApiDto,
  CommentItem,
  CommentMention,
  CommentMentionDto,
  CommentRecipient,
  CommentRecipientDto,
  CommentReply,
  CommentReplyDto,
  CommentScope,
  CommentStatus,
  CreateCommentDto,
  MentionType,
  ObjectCategory,
  UpdateCommentDto
} from './types'

export function decodeHtmlEntities(str?: string | null): string {
  if (!str || typeof str !== 'string') return ''
  let current = str
  let prev = ''
  for (let i = 0; i < 3 && current !== prev; i += 1) {
    prev = current
    current = current
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#(\d+);/g, (_, dec) => {
        const code = parseInt(dec, 10)
        return Number.isNaN(code) ? _ : String.fromCodePoint(code)
      })
      .replace(/&#x([0-9a-fA-F]+);/gi, (_, hex) => {
        const code = parseInt(hex, 16)
        return Number.isNaN(code) ? _ : String.fromCodePoint(code)
      })
  }
  return current
}

export class CommentMapper {
  /**
   * Normalizes category string to standard G | A | S | M | E
   */
  static normalizeCategory(cat?: string | null): ObjectCategory {
    const upper = (cat || 'G').trim().toUpperCase()
    if (['G', 'A', 'S', 'M', 'E'].includes(upper)) {
      return upper as ObjectCategory
    }
    return 'G'
  }

  /**
   * Returns standard category color hex
   */
  static getCategoryColor(cat?: string | null): string {
    const key = this.normalizeCategory(cat)
    const found = ANNOTATION_CATEGORIES.find((c) => c.key === key)
    return found?.colorHex || '#2563EB'
  }

  /**
   * Converts a backend CommentReply DTO to a domain CommentReply entity
   */
  static toReplyEntity(dto: CommentReplyDto): CommentReply {
    return {
      id: Number(dto.id),
      commentId: Number(dto.comment_id),
      authorId: Number(dto.author_id),
      author: dto.author,
      body: dto.body || '',
      s3Path: dto.s3_path || '',
      mentions: (dto.mentions || []).map(this.toMentionEntity),
      createdAt: dto.created_at || '',
      updatedAt: dto.updated_at || ''
    }
  }

  /**
   * Converts a backend CommentMention DTO to a domain CommentMention entity
   */
  static toMentionEntity(dto: CommentMentionDto): CommentMention {
    return {
      id: dto.id,
      commentId: Number(dto.comment_id || 0),
      replyId: dto.reply_id ? Number(dto.reply_id) : undefined,
      mentionType: dto.mention_type,
      targetId: dto.target_id ? Number(dto.target_id) : undefined
    }
  }

  /**
   * Converts a backend CommentRecipient DTO to a domain CommentRecipient entity
   */
  static toRecipientEntity(
    dto: CommentRecipientDto | number
  ): CommentRecipient {
    if (typeof dto === 'number') {
      return {
        targetType: 'comment',
        targetId: 0,
        annotationId: '',
        recipientType: 'user',
        recipientId: dto
      }
    }
    return {
      id: dto.id,
      targetType: dto.target_type || 'comment',
      targetId: Number(dto.target_id || 0),
      annotationId: String(dto.annotation_id || ''),
      recipientType: dto.recipient_type || 'user',
      recipientId: Number(dto.recipient_id),
      name: dto.name,
      email: dto.email
    }
  }

  /**
   * Converts a backend CommentApiDto to a strongly-typed domain CommentItem entity
   */
  static toDomainEntity(dto: CommentApiDto): CommentItem {
    const rawCat =
      dto.object_cat || dto.objectCat || dto.comment_object?.object_cat || 'G'

    const authorName =
      dto.creator_name ||
      dto.creatorName ||
      dto.comment_object?.creator_name ||
      (dto.author
        ? [dto.author.lastName, dto.author.firstName]
            .filter(Boolean)
            .join(' ') || (dto.author as any)?.name
        : '') ||
      '投稿者'

    const rawScope = dto.scope || dto.comment_object?.scope || 'common'
    const scope: CommentScope = ['personal', 'team', 'org', 'common'].includes(
      rawScope
    )
      ? (rawScope as CommentScope)
      : 'common'

    const rawStatus = dto.status || dto.comment_object?.status || 'open'
    const status: CommentStatus = rawStatus === 'resolved' ? 'resolved' : 'open'

    const h = Number(dto.pos_h ?? dto.posH ?? dto.h ?? dto.head_h ?? 0)
    const v = Number(dto.pos_v ?? dto.posV ?? dto.v ?? dto.head_v ?? 0)

    const rawYear = dto.year || dto.comment_object?.year || ''
    const subImageIdRaw = dto.sub_image_id ?? dto.subImageId
    const subImageId =
      subImageIdRaw !== undefined &&
      subImageIdRaw !== null &&
      String(subImageIdRaw) !== ''
        ? Number(subImageIdRaw)
        : null

    return {
      id: Number(dto.id),
      buildingId: Number(dto.building_id ?? dto.buildingId ?? 0),
      floorId: Number(dto.floor_id ?? dto.floorId ?? 0),
      pointId: String(dto.point_id ?? dto.pointId ?? ''),
      subImageId,
      sub_image_id: subImageId,
      annotationId: String(dto.annotation_id ?? dto.annotationId ?? dto.id),
      pointOrder: Number(dto.point_order ?? dto.pointOrder ?? 0),
      code: dto.code || undefined,
      title: decodeHtmlEntities(dto.title || ''),
      body: dto.body || '',
      s3Path: dto.s3_path ?? dto.s3Path ?? '',
      objectCat: this.normalizeCategory(rawCat),
      scope,
      status,
      authorId: Number(
        dto.author_id ?? dto.authorId ?? dto.comment_object?.created_by ?? 0
      ),
      author: dto.author,
      creatorName: authorName,
      posH: h,
      posV: v,
      year: rawYear ? String(rawYear).trim() : undefined,
      replies: (dto.replies || []).map((r) => this.toReplyEntity(r)),
      mentions: (dto.mentions || []).map((m) => this.toMentionEntity(m)),
      recipients: (dto.recipients || []).map((rc) =>
        this.toRecipientEntity(rc)
      ),
      createdAt: dto.created_at ?? dto.createdAt ?? '',
      updatedAt: dto.updated_at ?? dto.updatedAt ?? ''
    }
  }

  /**
   * Prepares CreateCommentDto payload for POST /v1/comments
   */
  static toCreateDto(params: {
    buildingId: number
    floorId: number
    pointId: string
    subImageId?: number | null
    annotationId: string
    pointOrder?: number
    title: string
    body?: string
    s3Path?: string
    objectCat: string
    scope: string
    status: string
    posH: number
    posV: number
    recipients?: number[]
    mentions?: {
      mention_type: MentionType
      target_id?: number
    }[]
  }): CreateCommentDto {
    return {
      building_id: params.buildingId,
      floor_id: params.floorId,
      point_id: params.pointId,
      sub_image_id: params.subImageId ?? undefined,
      annotation_id: params.annotationId,
      point_order: params.pointOrder,
      title: params.title.trim().slice(0, 255),
      body: params.body || '',
      s3_path: params.s3Path || undefined,
      object_cat: this.normalizeCategory(params.objectCat),
      scope: params.scope,
      status: params.status,
      pos_h: params.posH,
      pos_v: params.posV,
      recipients: params.recipients,
      mentions: params.mentions
    }
  }

  /**
   * Prepares UpdateCommentDto payload for PUT /v1/comments/{id}
   */
  static toUpdateDto(params: {
    title?: string
    body?: string
    s3Path?: string
    objectCat?: string
    scope?: string
    status?: string
    subImageId?: number | null
    recipients?: number[]
    mentions?: {
      mention_type: MentionType
      target_id?: number
    }[]
  }): UpdateCommentDto {
    const dto: UpdateCommentDto = {}
    if (params.title !== undefined)
      dto.title = params.title.trim().slice(0, 255)
    if (params.body !== undefined) dto.body = params.body
    if (params.s3Path !== undefined) dto.s3_path = params.s3Path
    if (params.objectCat !== undefined)
      dto.object_cat = this.normalizeCategory(params.objectCat)
    if (params.scope !== undefined) dto.scope = params.scope
    if (params.status !== undefined) dto.status = params.status
    if (params.subImageId !== undefined)
      dto.sub_image_id = params.subImageId ?? null
    if (params.recipients !== undefined) dto.recipients = params.recipients
    if (params.mentions !== undefined) dto.mentions = params.mentions
    return dto
  }
}

export default CommentMapper
